'use client';

import { Label, SectionFields } from '@/types/general';
import Stack from '@mui/material/Stack';
import useTranslations from '@/hooks/useTranslations';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextInput from './TextInput';
import CheckboxInput from './CheckboxInput';
import PasswordInput from './PasswordInput';
import dynamic from 'next/dynamic';
import WithCard from '@/components/WithCard';
import PasswordPicker from './PasswordPicker';

// The date-picker stack (~55 KiB) loads only for forms that render a date field
const DatepickerInput = dynamic(() => import('./DatepickerInput'));

export default function FormFields({
  formFields,
  showCard = true,
  disabled,
}: {
  formFields: SectionFields[];
  showCard?: boolean;
  disabled?: boolean;
}) {
  const { getLocale } = useTranslations();

  return (
    <WithCard show={showCard} sx={{ p: 1 }}>
      <Stack direction='column'>
        {formFields.map(({ label, fields }, index) => (
          <Stack
            direction='column'
            key={`section-${index}`}
            sx={{
              pb: 1,
            }}
          >
            {!!label && (
              <Typography
                variant='body1'
                noWrap
                component='h3'
                sx={{
                  color: 'text.primary',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 0.5,
                }}
              >
                {getLocale(label)}
              </Typography>
            )}

            <Grid container sx={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>
              {fields.map(
                (
                  {
                    field,
                    label,
                    type = 'text',
                    readOnly = false,
                    placeholder = '',
                    required = false,
                    multiline = false,
                    select = false,
                    options = [],
                    size = 4,
                    description = '',
                    defaultValue = '',
                    color,
                    onClick,
                    value,
                    error,
                    errorMessage,
                  },
                  index,
                ) => (
                  <Grid
                    key={`field-${index}`}
                    size={{
                      xs: 8,
                      sm: size,
                    }}
                  >
                    {type === 'checkbox' && field && label && (
                      <CheckboxInput
                        field={field}
                        label={label}
                        required={required}
                        description={description as Label}
                        color={color}
                        disabled={disabled}
                        onClick={onClick}
                      />
                    )}

                    {type === 'password' && field && label && (
                      <PasswordInput
                        field={field}
                        label={label}
                        required={required}
                        placeholder={placeholder as Label}
                        disabled={disabled}
                        error={error}
                        errorMessage={errorMessage}
                      />
                    )}

                    {type === 'datepicker' && field && (
                      <DatepickerInput
                        field={field}
                        label={label}
                        required={required}
                        placeholder={placeholder as Label}
                        readOnly={readOnly}
                        disabled={disabled}
                      />
                    )}

                    {type !== 'checkbox' &&
                      type !== 'password' &&
                      type !== 'datepicker' &&
                      field && (
                        <TextInput
                          field={field}
                          label={label}
                          type={type}
                          readOnly={readOnly}
                          placeholder={placeholder as Label}
                          required={required}
                          multiline={multiline}
                          select={select}
                          options={options}
                          defaultValue={defaultValue && defaultValue}
                          disabled={disabled}
                        />
                      )}

                    {type === 'passwordPicker' && (
                      <PasswordPicker
                        value={value as boolean}
                        onChange={onClick as (v: boolean) => void}
                      />
                    )}
                  </Grid>
                ),
              )}
            </Grid>
          </Stack>
        ))}
      </Stack>
    </WithCard>
  );
}
